import { API_BASE } from "../../../consts";
import authService from "../../../services/AuthService";

const authFetch = (path, options = {}) =>
    authService.authenticatedFetch(`${API_BASE}${path}`, options);

const asJson = async (res) => {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err = new Error(data.error || `Request failed: ${res.status}`);
        err.status = res.status;
        throw err;
    }
    return data;
};

// ── Catalog ──
// Retries one sync-page call a few times with backoff before giving up —
// the backend already retries internally per-request, this is the outer
// net for when even those attempts are exhausted (sustained outage, etc.),
// so a single bad page never kills an entire sync loop outright.
export const withPageRetry = async (fn, { attempts = 5, baseDelayMs = 1000, retryableStatuses = null } = {}) => {
    let lastErr;
    for (let i = 0; i < attempts; i++) {
        try {
            return await fn();
        } catch (e) {
            if (retryableStatuses && e.status && !retryableStatuses.includes(e.status)) {
                throw e;
            }
            lastErr = e;
            const wait = Math.min(baseDelayMs * 2 ** i, 15000);
            await new Promise((r) => setTimeout(r, wait));
        }
    }
    throw lastErr;
};

export const listCatalog = (params = {}) => {
    const normalized = { ...params };
    if (Array.isArray(normalized.unpublished_reason_groups)) {
        normalized.unpublished_reason_groups = normalized.unpublished_reason_groups.join("\x1f");
    }
    const qs = new URLSearchParams(
        Object.entries(normalized).filter(([, v]) => v !== undefined && v !== ""),
    ).toString();
    return authFetch(`/api/catalog?${qs}`).then(asJson);
};

export const getCatalogItem = (id) => authFetch(`/api/catalog/${id}`).then(asJson);

export const updateCatalogItemNotes = (itemId, notes) =>
    authFetch(`/api/catalog/${itemId}/notes`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes }),
    }).then(asJson);

// ── Walmart sync ──
export const getLivePriceIncentives = () =>
    authFetch(`/api/walmart/price-incentives`).then(asJson);

export const syncWalmartItems = (cursor) =>
    authFetch(`/api/walmart/catalog/sync-items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cursor }),
    }).then(asJson);

export const getSfInventory = (sku) =>
    authFetch(`/api/walmart/api/inventory/sf?sku=${encodeURIComponent(sku)}`).then(asJson);

export const getWfsInventory = (sku) =>
    authFetch(`/api/walmart/api/inventory/wfs?sku=${encodeURIComponent(sku)}`).then(asJson);

export const getItemDetails = (catalogItemId) =>
    authFetch(`/api/walmart/api/item-details?catalog_item_id=${encodeURIComponent(catalogItemId)}`).then(asJson);

export const syncInventorySfPage = (cursor) =>
    authFetch(`/api/walmart/catalog/sync-inventory-sf-page`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cursor }),
    }).then(asJson);

export const syncInventoryWfsPage = (offset) =>
    authFetch(`/api/walmart/catalog/sync-inventory-wfs-page`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ offset }),
    }).then(asJson);

export const captureInventorySnapshot = () =>
    authService.authenticatedFetch(`${API_BASE}/api/reports/capture-inventory-snapshot`, { method: "POST" })
        .then((r) => r.json());

export const syncOrdersPage = async (shipNode, cursor, opts = {}) => {
    const resp = await authService.authenticatedFetch(`${API_BASE}/api/reports/sync-orders-page`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            ship_node: shipNode, cursor: cursor || null,
            is_chunk: opts.isChunk || false,
            chunk_start: opts.chunkStart || null,
            chunk_end: opts.chunkEnd || null,
        }),
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data.error || `Request failed: ${resp.status}`);
    return data;
};

export const getOrderSyncStatus = async () => {
    const resp = await authService.authenticatedFetch(`${API_BASE}/api/reports/sync-orders-status`);
    const data = await resp.json();
    if (!resp.ok) throw new Error(data.error || `Request failed: ${resp.status}`);
    return data;
};

export const markOrdersBackfilled = async (shipNode) => {
    const resp = await authService.authenticatedFetch(`${API_BASE}/api/reports/sync-orders-mark-backfilled`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ship_node: shipNode }),
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data.error || `Request failed: ${resp.status}`);
    return data;
};

export const finalizeOrderSync = async () => {
    const resp = await authService.authenticatedFetch(`${API_BASE}/api/reports/sync-orders-finalize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data.error || `Request failed: ${resp.status}`);
    return data;
};

export const syncShipmentsPage = async (offset, limit, daysBack) => {
    const resp = await authService.authenticatedFetch(`${API_BASE}/api/wfs/shipments/sync-page`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ offset: offset || 0, limit: limit || 20, days_back: daysBack || 365 }),
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data.error || `Request failed: ${resp.status}`);
    return data;
};

export const updateItemPrice = (catalogItemId, sku, price) =>
    authFetch(`/api/walmart/api/price`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ catalog_item_id: catalogItemId, sku, price }),
    }).then(asJson);

export const updateSfInventory = (catalogItemId, sku, quantity) =>
    authFetch(`/api/walmart/api/inventory/sf`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ catalog_item_id: catalogItemId, sku, quantity }),
    }).then(asJson);

export const bulkSetPrice = (catalogItemIds, price) =>
    authFetch(`/api/catalog/bulk-price`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ catalog_item_ids: catalogItemIds, price }),
    }).then(asJson);

export const bulkSetSfInventory = (catalogItemIds, quantity) =>
    authFetch(`/api/catalog/bulk-inventory`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ catalog_item_ids: catalogItemIds, quantity }),
    }).then(asJson);

// ── Cost definitions ──
export const listCostDefinitions = () => authFetch(`/api/costs/definitions`).then(asJson);

export const createCostDefinition = (name) =>
    authFetch(`/api/costs/definitions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
    }).then(asJson);

export const updateCostDefinition = (id, name) =>
    authFetch(`/api/costs/definitions/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
    }).then(asJson);

export const deleteCostDefinition = (id) =>
    authFetch(`/api/costs/definitions/${id}`, { method: "DELETE" }).then(asJson);

export const setItemCosts = (catalogItemIds, costDefinitionId, value) =>
    authFetch(`/api/costs/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            catalog_item_ids: catalogItemIds,
            cost_definition_id: costDefinitionId,
            value,
        }),
    }).then(asJson);

export const removeItemCosts = (catalogItemIds, costDefinitionId) =>
    authFetch(`/api/costs/items`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            catalog_item_ids: catalogItemIds,
            cost_definition_id: costDefinitionId,
        }),
    }).then(asJson);

// ── Flag definitions ──
export const listFlagDefinitions = () => authFetch(`/api/flags/definitions`).then(asJson);

export const createFlagDefinition = (name, color) =>
    authFetch(`/api/flags/definitions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, color }),
    }).then(asJson);

export const updateFlagDefinition = (id, updates) =>
    authFetch(`/api/flags/definitions/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
    }).then(asJson);

export const deleteFlagDefinition = (id) =>
    authFetch(`/api/flags/definitions/${id}`, { method: "DELETE" }).then(asJson);

export const assignItemFlags = (catalogItemIds, flagDefinitionId) =>
    authFetch(`/api/flags/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            catalog_item_ids: catalogItemIds,
            flag_definition_id: flagDefinitionId,
        }),
    }).then(asJson);

export const removeItemFlags = (catalogItemIds, flagDefinitionId) =>
    authFetch(`/api/flags/items`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            catalog_item_ids: catalogItemIds,
            flag_definition_id: flagDefinitionId,
        }),
    }).then(asJson);

// ── Custom field definitions (free-text, alphanumeric) ──
export const listFieldDefinitions = () => authFetch(`/api/custom-fields/definitions`).then(asJson);

export const createFieldDefinition = (name) =>
    authFetch(`/api/custom-fields/definitions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
    }).then(asJson);

export const updateFieldDefinition = (id, name) =>
    authFetch(`/api/custom-fields/definitions/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
    }).then(asJson);

export const deleteFieldDefinition = (id) =>
    authFetch(`/api/custom-fields/definitions/${id}`, { method: "DELETE" }).then(asJson);

export const setItemFields = (catalogItemIds, fieldDefinitionId, value) =>
    authFetch(`/api/custom-fields/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            catalog_item_ids: catalogItemIds,
            field_definition_id: fieldDefinitionId,
            value,
        }),
    }).then(asJson);

export const removeItemFields = (catalogItemIds, fieldDefinitionId) =>
    authFetch(`/api/custom-fields/items`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            catalog_item_ids: catalogItemIds,
            field_definition_id: fieldDefinitionId,
        }),
    }).then(asJson);

// ── Import ──
export const commitImport = (payload) =>
    authFetch(`/api/import/commit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    }).then(asJson);


// ── Batches ──
export const createBatch = (name, batchType) =>
    authFetch(`/api/batches`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, batch_type: batchType }),
    }).then(asJson);

export const listBatches = (status = "open") =>
    authFetch(`/api/batches?status=${encodeURIComponent(status)}`).then(asJson);

export const getBatch = (batchId) => authFetch(`/api/batches/${batchId}`).then(asJson);

export const deleteBatch = (batchId) =>
    authFetch(`/api/batches/${batchId}`, { method: "DELETE" }).then(asJson);

export const listBatchItems = (batchId) =>
    authFetch(`/api/batches/${batchId}/items`).then(asJson);

export const addBatchItem = (batchId, item) =>
    authFetch(`/api/batches/${batchId}/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(item),
    }).then(asJson);

export const removeBatchItem = (batchId, itemId) =>
    authFetch(`/api/batches/${batchId}/items/${itemId}`, { method: "DELETE" }).then(asJson);

export const flushBatch = (batchId, feedType) =>
    authFetch(`/api/batches/${batchId}/flush`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feed_type: feedType }),
    }).then(asJson);

export const updateCatalogItemMeta = (itemId, updates) =>
    authFetch(`/api/catalog/${itemId}/meta`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
    }).then(asJson);

export const checkBatchProcessing = (batchId) =>
    authFetch(`/api/batches/${batchId}/check-processing`, { method: "POST" }).then(asJson);

export const updateBatchItemQuantity = (batchId, itemId, quantity) =>
    authFetch(`/api/batches/${batchId}/items/${itemId}/quantity`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quantity }),
    }).then(asJson);

export const updateBatchItemPrice = (batchId, itemId, price) =>
    authFetch(`/api/batches/${batchId}/items/${itemId}/price`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ price }),
    }).then(asJson);

export const updateBatchItemSku = (batchId, itemId, sku) =>
    authFetch(`/api/batches/${batchId}/items/${itemId}/sku`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sku }),
    }).then(asJson);

const reportsApi = (path, params) => {
    const qs = params ? `?${new URLSearchParams(params).toString()}` : "";
    return authService.authenticatedFetch(`${API_BASE}${path}${qs}`).then(async (resp) => {
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || `Request failed: ${resp.status}`);
        return data;
    });
};

export const getValuationReport = (groupBy = "fulfillment", basis = "cost") =>
    reportsApi("/api/reports/valuation", { group_by: groupBy, basis });

export const getValuationHistory = (asOf, groupBy = "fulfillment", basis = "cost") =>
    reportsApi("/api/reports/valuation-history", { as_of: asOf, group_by: groupBy, basis });

export const getRestockAlerts = (thresholdDays = 14) =>
    reportsApi("/api/reports/restock-alerts", { threshold_days: thresholdDays });

export const getSfNotZeroed = () => reportsApi("/api/reports/sf-not-zeroed");

export const getSkuDetail = (catalogItemId, rangeParams) =>
    reportsApi(`/api/reports/sku/${catalogItemId}`, rangeParams);

export const getPnlReport = (rangeParams) => reportsApi("/api/reports/pnl", rangeParams);
export const getSalesTaxReport = (rangeParams) => reportsApi("/api/reports/sales-tax", rangeParams);
export const getSupplierReport = (rangeParams) => reportsApi("/api/reports/supplier", rangeParams);
export const getExpirationsReport = () => reportsApi("/api/reports/expirations");
export const getStorageReport = () => reportsApi("/api/reports/storage");
export const getReportLineItems = (params = {}) =>
    reportsApi("/api/reports/line-items", Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
    ));

export const getUnpublishedReasons = () => authFetch(`/api/catalog/unpublished-reasons`).then(asJson);

export const getItemsMetadata = (ids) => {
    if (!ids || ids.length === 0) return Promise.resolve({ items: {} });
    return authFetch(`/api/catalog/items-metadata`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
    }).then(asJson);
};
export const relinkOrphanedOrdersPage = async () => {
    const resp = await authService.authenticatedFetch(`${API_BASE}/api/orders/relink-orphaned-items-page`, { method: "POST" });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data.error || `Request failed: ${resp.status}`);
    return data;
};

export const getItemFees = (itemId) =>
    authFetch(`/api/catalog/${itemId}/fees`).then(asJson);

export const saveItemFees = (itemId, feeBreakdown, totalFees, source = "manual") =>
    authFetch(`/api/catalog/${itemId}/fees`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fee_breakdown: feeBreakdown, total_fees: totalFees, source }),
    }).then(asJson);

export const getItemsFeesBatch = (ids) => {
    if (!ids || ids.length === 0) return Promise.resolve({ fees: {} });
    return authFetch(`/api/catalog/items-fees`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
    }).then(asJson);
};

export const mergeItemFeeKey = (itemId, key, value, source = "manual") =>
    authFetch(`/api/catalog/${itemId}/fees/merge`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key, value, source }),
    }).then(asJson);

export const getMissingFeeItems = (filters = {}, options = {}) => {
    const normalized = { ...filters };
    if (Array.isArray(normalized.unpublished_reason_groups)) {
        normalized.unpublished_reason_groups = normalized.unpublished_reason_groups.join("\x1f");
    }
    if (options.includeExisting) normalized.include_existing = "true";
    const qs = new URLSearchParams(
        Object.entries(normalized).filter(([, v]) => v !== undefined && v !== "" && v !== null)
    ).toString();
    return authFetch(`/api/catalog/backfill-fees/missing?${qs}`).then(asJson);
};

export const backfillSfFeesPage = (settings, filters, offset, limit = 200, options = {}) =>
    authFetch(`/api/catalog/backfill-fees/bulk-sf-page`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            ...settings, filters, offset, limit,
            include_existing: !!options.includeExisting,
        }),
    }).then(asJson);

export const backfillOneWfsFee = (itemId, settings, options = {}) =>
    authFetch(`/api/catalog/${itemId}/backfill-fee`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...settings, force: !!options.force }),
    }).then(asJson);

export const getReconReport = (reportDate, offset = 0, noOfRecords = 200) => {
    const params = new URLSearchParams({ report_date: reportDate, offset, no_of_records: noOfRecords });
    return authFetch(`/api/reports/recon?${params}`).then(asJson);
};

export const retireCatalogItem = (itemId) =>
    authFetch(`/api/catalog/${itemId}/retire`, { method: "DELETE" }).then(asJson);